import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { Select as GuiSelect } from '@oliasoft-open-source/react-ui-library';
import translations from 'src/internationalisation/translation-map.json';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';

/**
 * RawSelect is a customizable select input component used for rendering
 * a selection dropdown in forms.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.name - The name of the field used for form control and identification.
 * @param {Array} props.options - The options to display in the select dropdown, provided as an array of objects.
 * @param {Object} props.control - The control object for form state management (e.g., from react-hook-form).
 * @param {Object} props.errors - The object containing validation error messages mapped to field names.
 * @param {number} [props.width=0] - The width of the select dropdown. Default is 0 which implies auto-sizing.
 * @param {Array|null} [props.groupOrder=null] - Optional grouping order for categorizing dropdown options.
 * @param {Function} [props.onChange=(e) => {}] - The callback function triggered on value change.
 * @param {string|null} [props.placeholder=null] - Optional placeholder text displayed when no option is selected.
 * @param {boolean} [props.disabled=false] - Whether the component is disabled and non-interactive. Default is false.
 * @param {boolean} [props.clearable=false] - Indicates if the selected value can be cleared. Default is false.
 * @param {boolean} [props.multi=false] - Enables multi-select functionality. Default is false.
 * @param {string|null} [props.testId=null] - Optional testing identifier for query selectors in tests.
 * @param {boolean} [props.autoLayerWidth=false] - Controls automatic adjustment of dropdown width to match the select field width. Default is false.
 * @returns {JSX.Element} The rendered select dropdown component.
 */
const RawSelect = ({
  name,
  options,
  control,
  errors,
  width = 0,
  groupOrder = null,
  onChange = () => {},
  placeholder = null,
  disabled = false,
  clearable = false,
  multi = false,
  testId = null,
  autoLayerWidth = false,
}) => {
  const { t } = useTranslation();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const { onChange: fieldOnChange, ...props } = field;
        delete props.ref;

        return (
          <GuiSelect
            {...props}
            options={options}
            width={width}
            groupOrder={groupOrder}
            disabled={disabled}
            clearable={clearable}
            multi={multi}
            testId={testId}
            autoLayerWidth={autoLayerWidth}
            placeholder={placeholder}
            onChange={(e) => {
              fieldOnChange(e.target.value);
              if (onChange) {
                onChange(e);
              }
            }}
            error={translations[errors[name]] ? t(errors[name]) : errors[name] || null}
          />
        );
      }}
    />
  );
};

export const Select = withErrorBoundary(RawSelect);
